import React, { useState } from 'react';
import { useAppContext } from '../context/AppContext';
import { api } from '../services/api';
import SafeIcon from '../common/SafeIcon';

const Customers = () => {
  const { data, refreshData, t } = useAppContext();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);

  const handleSave = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const customer = {
      id: editingCustomer?.id || '',
      name: formData.get('name'),
      contact_name: formData.get('contact_name'),
      email: formData.get('email'),
      phone: formData.get('phone'),
      delivery_days: formData.get('delivery_days'),
      customer_type: formData.get('customer_type'),
      slug: formData.get('slug') || '',
      notes: formData.get('notes'),
      active: formData.get('active') === 'on'
    };

    await api.saveCustomer(customer);
    await refreshData();
    setIsModalOpen(false);
  };

  const handleDelete = async (id, name) => {
    const confirmed = window.confirm(
      t('products.deleteConfirm').replace('{name}', name)
    );
    if (confirmed) {
      await api.deleteCustomer(id);
      await refreshData();
    }
  };

  /**
   * Helper to copy the standalone portal URL
   */
  const copyPortalUrl = (slug) => {
    const url = `${window.location.origin}/portal/#/${slug}`;
    navigator.clipboard.writeText(url);
    alert('Portal link copied to clipboard!');
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-end">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('customers.title')}</h1>
          <p className="text-gray-500 mt-1">{t('customers.subtitle')}</p>
        </div>
        <button 
          onClick={() => { setEditingCustomer(null); setIsModalOpen(true); }}
          className="bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2"
        >
          <SafeIcon name="Plus" className="w-4 h-4" />
          {t('customers.addCustomer')}
        </button>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {data.customers.map((c) => (
          <div 
            key={c.id} 
            className={`bg-white rounded-xl border p-5 shadow-sm transition-all group ${c.active ? 'border-gray-200' : 'border-red-100 bg-red-50/30 opacity-75'}`}
          >
            <div className="flex justify-between items-start mb-3">
              <h3 className="font-bold text-lg text-gray-900 truncate pr-4">{c.name}</h3>
              <div className="flex items-center gap-2 shrink-0">
                <button 
                  onClick={() => { setEditingCustomer(c); setIsModalOpen(true); }}
                  className="text-gray-400 hover:text-amber-600 transition-colors"
                  title={t('actions.edit')}
                >
                  <SafeIcon name="Edit2" className="w-4 h-4" />
                </button>
                <button 
                  onClick={() => handleDelete(c.id, c.name)}
                  className="text-gray-300 hover:text-red-600 transition-colors"
                  title={t('actions.delete')}
                >
                  <SafeIcon name="Trash2" className="w-4 h-4" />
                </button>
              </div>
            </div>
            
            <div className="space-y-2 text-sm text-gray-600">
              <p className="flex items-center gap-2">
                <SafeIcon name="User" className="w-4 h-4 text-gray-400" /> {c.contact_name}
              </p>
              <p className="flex items-center gap-2">
                <SafeIcon name="Mail" className="w-4 h-4 text-gray-400" /> {c.email}
              </p>
              <p className="flex items-center gap-2">
                <SafeIcon name="Phone" className="w-4 h-4 text-gray-400" /> {c.phone}
              </p>
              <p className="flex items-center gap-2">
                <SafeIcon name="Truck" className="w-4 h-4 text-gray-400" /> {c.delivery_days}
              </p>
              {c.slug && (
                <div className="pt-2 flex items-center justify-between">
                  <p className="flex items-center gap-2 text-amber-700 font-bold">
                    <SafeIcon name="Link" className="w-4 h-4" />
                    {c.slug}
                  </p>
                  <button 
                    onClick={() => copyPortalUrl(c.slug)}
                    className="text-[10px] uppercase font-black tracking-widest text-gray-400 hover:text-amber-600 transition-colors"
                  >
                    Copy Link
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-lg w-full p-6 shadow-xl">
            <h2 className="text-xl font-bold mb-4">
              {editingCustomer ? t('customers.form.titleEdit') : t('customers.form.titleAdd')}
            </h2>
            <form onSubmit={handleSave} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('customers.form.businessName')}</label>
                <input required name="name" defaultValue={editingCustomer?.name} className="w-full border border-gray-300 rounded-lg p-2 outline-none focus:border-amber-500" />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('customers.form.contactName')}</label>
                  <input name="contact_name" defaultValue={editingCustomer?.contact_name} className="w-full border border-gray-300 rounded-lg p-2 outline-none focus:border-amber-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('customers.form.type')}</label>
                  <select name="customer_type" defaultValue={editingCustomer?.customer_type || 'Wholesale'} className="w-full border border-gray-300 rounded-lg p-2 outline-none focus:border-amber-500">
                    <option value="Wholesale">{t('customers.types.wholesale')}</option>
                    <option value="Retail">{t('customers.types.retail')}</option>
                    <option value="Internal">{t('customers.types.internal')}</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('customers.form.email')}</label>
                  <input type="email" name="email" defaultValue={editingCustomer?.email} className="w-full border border-gray-300 rounded-lg p-2 outline-none focus:border-amber-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('customers.form.phone')}</label>
                  <input name="phone" defaultValue={editingCustomer?.phone} className="w-full border border-gray-300 rounded-lg p-2 outline-none focus:border-amber-500" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('customers.form.deliveryDays')}</label>
                  <input name="delivery_days" defaultValue={editingCustomer?.delivery_days} placeholder="e.g. Mon, Wed, Fri" className="w-full border border-gray-300 rounded-lg p-2 outline-none focus:border-amber-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 text-amber-700 font-bold">{t('customers.form.slug')}</label>
                  <input name="slug" defaultValue={editingCustomer?.slug} placeholder="cafe-central-123" className="w-full border border-amber-300 rounded-lg p-2 outline-none focus:border-amber-500" />
                </div>
              </div>

              <div>
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <input type="checkbox" name="active" defaultChecked={editingCustomer ? editingCustomer.active : true} className="w-4 h-4 text-amber-600 rounded" />
                  {t('customers.form.isActive')}
                </label>
              </div>

              <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-100">
                <button type="button" onClick={() => setIsModalOpen(false)} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg font-medium">
                  {t('actions.cancel')}
                </button>
                <button type="submit" className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 font-medium">
                  {t('customers.form.save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Customers;